import {
  getGuestKey,
  GuestModel,
  useUpdateDescriptionGuest,
} from 'api-hooks/guest';
import { getHotelSalesKey } from 'api-hooks/sale/hotel';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { CardContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

interface Props {
  guest: GuestModel;
}
export default function DescriptionForm(props: Props) {
  const { guest } = props;
  const { t } = useTranslation();
  const { mutateAsync } = useUpdateDescriptionGuest();
  const { can } = useAuthorization();

  const defaultValues = React.useMemo(
    () => ({
      description: guest.description ?? '',
    }),
    [guest],
  );

  const yupSchema = React.useMemo(
    () => Yup.object().shape({ description: Yup.string().nullable() }),
    [],
  );

  const resolver = useYupValidationResolver(yupSchema);
  const methods = useForm({
    defaultValues,
    resolver,
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        const result = await mutateAsync({ guestId: guest.id, body: values });

        methods.reset();

        queryClient.refetchQueries([getHotelSalesKey()[0]]);
        queryClient.refetchQueries(
          getGuestKey({
            id: guest.id,
          }),
        );
        notification.success({ message: result?.message });
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [guest, methods, mutateAsync],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!guest}
      noReset
    >
      <CardContainer>
        <FormHeader
          title={t('common:description')}
          data={guest}
          noDelete={!!guest}
          onDelete={() => {}}
          //TODO CHANGE ACL
          noEdit={!can(AuthorizationRules.GuestsUpdateDescription)}
        />

        <FormState>
          {({ editable }) => {
            if (!editable && !guest.description) return null;
            return (
              <Input
                key={+new Date()}
                type="editor"
                name="description"
                label=""
                controls={['default']}
                overrides={
                  !editable
                    ? {
                        root: {
                          backgroundColor: 'transparent',
                          borderWidth: 0,
                        },
                        content: {
                          backgroundColor: 'transparent',
                          borderWidth: 0,
                          minHeight: 0,
                        },
                      }
                    : undefined
                }
              />
            );
          }}
        </FormState>
      </CardContainer>
    </Form>
  );
}
